import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authService } from '../services/api';
import { useSessionTimeout } from '../hooks/useSessionTimeout';
import { auditLog } from '../api/audit';

export const SESSION_TIMEOUT_MINUTES = 15;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const setAuthSession = useCallback(({ loggedInUser, access, refresh }) => {
    if (access) localStorage.setItem('access_token', access);
    if (refresh) localStorage.setItem('refresh_token', refresh);
    if (loggedInUser) setUser(loggedInUser);
  }, []);

  useEffect(() => {
    // Check if user is logged in on mount
    const loadUser = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          const response = await authService.getProfile();
          setUser(response.data);
        } catch (error) {
          console.error('Failed to load user:', error);
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
        }
      }
      setLoading(false);
    };

    loadUser();
  }, []);

  const logout = useCallback((reason = 'user_action') => {
    auditLog('logout', 'success', { reason });
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
  }, []);

  // Session timeout: 15-min inactivity guard (Section 15)
  // onWarning fires 60 s before expiry via the hook's internal interval
  useSessionTimeout({
    timeoutMinutes: SESSION_TIMEOUT_MINUTES,
    enabled: !!user,
    onTimeout: () => {
      logout('inactivity');
      // Navigate to login with a session-expired flag so the login page
      // can show "You were signed out due to inactivity."
      window.location.href = '/login?expired=1';
    },
    onWarning: (secondsLeft) => {
      // Dispatch a custom DOM event — ToastContext (or any listener) can subscribe
      window.dispatchEvent(
        new CustomEvent('pww:session-warning', { detail: { secondsLeft } }),
      );
    },
    warningSeconds: 60,
  });

  const login = async (email, password, otp) => {
    const payload = { email, password };
    if (otp) payload.otp = otp;
    const response = await authService.login(payload);
    const { user: loggedInUser, access, refresh } = response.data;

    setAuthSession({ loggedInUser, access, refresh });

    auditLog('login', 'success', { userId: loggedInUser?.id });
    return loggedInUser;
  };

  const sendEmailVerificationCode = async ({ email }) => {
    const response = await authService.sendEmailVerificationCode({ email });
    return response.data;
  };

  const verifyEmailCodeAndLogin = async ({ email, otp }) => {
    const response = await authService.verifyEmailCode({ email, otp });
    const { user: loggedInUser, access, refresh } = response.data;
    setAuthSession({ loggedInUser, access, refresh });
    return loggedInUser;
  };

  const register = async (data) => {
    const response = await authService.register(data);
    // Don't auto-login after registration - user will be redirected to login page
    return response.data;
  };

  const sendPhoneVerificationCode = async ({ phone_number, country }) => {
    const response = await authService.sendPhoneVerificationCode({ phone_number, country });
    return response.data;
  };

  const verifyPhoneCodeAndRegister = async (data) => {
    const response = await authService.verifyPhoneCodeAndRegister(data);
    const { user: createdUser, access, refresh } = response.data;

    setAuthSession({ loggedInUser: createdUser, access, refresh });
    return createdUser;
  };

  const value = {
    user,
    loading,
    login,
    register,
    sendEmailVerificationCode,
    verifyEmailCodeAndLogin,
    sendPhoneVerificationCode,
    verifyPhoneCodeAndRegister,
    logout,
    isAuthenticated: !!user,
    sessionTimeoutMinutes: SESSION_TIMEOUT_MINUTES,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;

